import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import React, { ReactNode } from "react";

interface MarkdownRendererProps {
  content: string;
  maxHeight: string;
  prose?: boolean;
  highlightCitations?: boolean;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  maxHeight,
  prose = true,
  highlightCitations = false,
}) => {
  const processWithCitationHighlighting = (text: string): ReactNode => {
    if (!text) return "";

    const parts = text.split(/(\[\d+(?:,\s*\d+)*\])/g);

    return parts.map((part, index) => {
      if (/\[\d+(?:,\s*\d+)*\]/.test(part)) {
        return (
          <span
            key={index}
            className="text-brand-primary font-semibold bg-green-50 px-1 rounded"
          >
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const processNodeChildren = (children: ReactNode): ReactNode => {
    if (typeof children === "string") {
      return processWithCitationHighlighting(children);
    }

    if (Array.isArray(children)) {
      return children.map((child, index) => {
        if (typeof child === "string") {
          return processWithCitationHighlighting(child);
        }
        return child;
      });
    }

    return children;
  };

  const createElementHandler = (Component: React.ElementType) => {
    return ({
      node,
      children,
      ...props
    }: {
      node: any;
      children: ReactNode;
      [key: string]: any;
    }) => {
      return <Component {...props}>{processNodeChildren(children)}</Component>;
    };
  };

  const components = highlightCitations
    ? {
        p: createElementHandler("p"),
        li: createElementHandler("li"),
        h1: createElementHandler("h1"),
        h2: createElementHandler("h2"),
        h3: createElementHandler("h3"),
        h4: createElementHandler("h4"),
        h5: createElementHandler("h5"),
        h6: createElementHandler("h6"),
        blockquote: createElementHandler("blockquote"),
        em: createElementHandler("em"),
        strong: createElementHandler("strong"),
        td: createElementHandler("td"),
      }
    : {};

  return (
    <>
      {prose ? (
        <div className={`prose max-w-none ${maxHeight} overflow-auto`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={components as any}
          >
            {content}
          </ReactMarkdown>
        </div>
      ) : (
        <div dangerouslySetInnerHTML={{ __html: content }} />
      )}
    </>
  );
};

export default MarkdownRenderer;
